import { ActionModal } from '@kcftech/design-system';
import { IAssemblyApplicationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import {
    AssemblyApplicationFormContent,
    AssemblyApplicationFormData,
} from 'pages/Settings/AssemblyType/AssemblyApplicationFormContent';
import { useForm } from 'react-hook-form';
import { duplicateAssemblyApplication } from 'utils/assembly_application';

export type DuplicateAssemblyApplicationModalProps = Readonly<{
    assemblyTypeId: number;
    onClose: (duplicatedApplication: IAssemblyApplicationResponse | undefined) => void;
    application: IAssemblyApplicationResponse;
    siblings: IAssemblyApplicationResponse[];
}>;

export function DuplicateAssemblyApplicationModal({
    assemblyTypeId,
    siblings,
    onClose,
    application,
}: DuplicateAssemblyApplicationModalProps) {
    const siblingNames = siblings.map((sibling) => sibling.Name);
    const siblingCodes = siblings.map((sibling) => sibling.Code);

    const { showToast } = useNotificationContext();

    const form = useForm<AssemblyApplicationFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
            code: '',
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
    } = form;

    const { mutate: duplicateApplication, isPending } = useMutation({
        mutationFn: async (formData: AssemblyApplicationFormData) =>
            duplicateAssemblyApplication(application.Id, assemblyTypeId, formData),

        onSuccess: (result) => {
            onClose(result);
            showToast({
                type: 'success',
                content: 'Successfully duplicated assembly application',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to duplicate assembly application',
            }),
    });

    const onSave = handleSubmit((formData) => duplicateApplication(formData));

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={() => onClose(undefined)}
            title="Duplicate Assembly Application"
            height={350}
            actionButtonText="Duplicate"
            closeButtonText="Cancel"
        >
            <AssemblyApplicationFormContent
                form={form}
                siblingNames={siblingNames}
                siblingCodes={siblingCodes}
            />
        </ActionModal>
    );
}
